import { useState } from "react";
import { post } from "../../api/client.ts";
import type { FlatRateSummary } from "../../api/types.ts";
import { useBrand } from "../../state/BrandContext.tsx";

export function flatRatePrice(plan: FlatRateSummary): string {
  return new Intl.NumberFormat("en-IE", { style: "currency", currency: plan.currency, maximumFractionDigits: 0 }).format(plan.amountCents / 100);
}

export function FlatRatePlan({ plan }: { plan: FlatRateSummary }) {
  const { brand } = useBrand();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const owner = brand?.role === "owner";
  const manage = plan.active || !["pending", "canceled", "incomplete_expired"].includes(plan.status);
  async function openBilling() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await post<{ url: string }>(manage ? "/api/stripe/portal" : "/api/stripe/flat-rate/checkout");
      if (!url || !/^https:\/\//i.test(url)) throw new Error("Billing is unavailable. Please try again.");
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not open billing. Please try again.");
      setBusy(false);
    }
  }
  return <>
    <header className="page-head fade-in"><h1 className="t-h2">Your plan</h1></header>
    <section className="card fade-in spend-new" aria-label="Flat-rate plan" style={{ marginTop: 24, maxWidth: 640 }}>
      <div className="spend-heading"><span className="t-eyebrow">{plan.cardLimit}-card flat rate</span><span className="spend-status">{plan.active ? "Active" : manage ? "Payment required" : "Activation required"}</span></div>
      <div className="t-display" style={{ margin: "16px 0 4px", fontSize: 44 }}>{flatRatePrice(plan)}</div>
      <p className="t-body-sm">{plan.intervalLabel} · {plan.cardLimit} cards per paid cycle</p>
      <div className="bill-tear" />
      {plan.active ? <div className="spend-threshold" style={{ marginTop: 0, paddingTop: 0, borderTop: "none" }}>
        <div className="spend-heading"><strong>{plan.remaining} cards remaining</strong><span>{plan.used} of {plan.cardLimit} used</span></div>
        <progress aria-label="Cards used this cycle" max={plan.cardLimit} value={plan.used}  />
        {plan.periodEnd && <p className="spend-caption">Current cycle ends {new Date(plan.periodEnd).toLocaleDateString("en-IE", { day: "numeric", month: "long", year: "numeric" })}.</p>}
        <p className="t-body-sm" style={{ marginTop: 16 }}>Each creative uses one card. Briefs go straight to Work, included in your plan.</p>
      </div> : <p className="t-body-sm">Your board is open. {manage ? "Update your subscription payment to resume new briefs." : "Activate your plan to send new briefs directly to production."}</p>}
      {owner ? <button className={`btn ${manage ? "btn-ghost" : "btn-brand"}`} style={{ marginTop: 24 }} disabled={busy} onClick={() => void openBilling()}>
        {busy ? "Opening secure billing…" : manage ? "Manage subscription" : `Activate · ${flatRatePrice(plan)} / ${plan.intervalLabel}`}
      </button> : <p className="t-body-sm" style={{ marginTop: 24 }}>Only your brand owner can activate or manage this plan.</p>}
      {error && <p className="t-body-sm" role="alert" style={{ marginTop: 16 }}>{error}</p>}
    </section>
  </>;
}
